
/* ============================== src/utils/helpers.js ============================== */
// utils/helpers.js
//
// Утилиты представления сотрудника.
//
// Два разных сценария отображения - две разные функции:
//
//   - getPositionInDept(employee, deptId) - Staff, где сотрудник
//     показан в контексте конкретного отдела. Возвращает ОДНУ должность
//     - ту, что у него в этом отделе. Название отдела не возвращает:
//     оно уже в заголовке секции.
//
//   - buildEmployeePositions(employee, db) - Cabinet и другие места,
//     где нужен ПОЛНЫЙ список мест работы с пометкой основное/совм.
//
// Обе опираются на одни и те же поля: employee.position (личная
// должность), employee.departments[i] = { deptId, primary, position, rate },
// роль kb_chief и employee.kbIds.
//
// MOD: для сотрудников без отдела/КБ (директор, главный бухгалтер,
// руководство без привязки) «основное подразделение» - название
// компании. Единая точка - COMPANY_NAME в constants.js; правило
// реализовано в getEmployeePrimaryDepartment и buildEmployeePositions,
// чтобы все потребители (EmployeeCardModal, Cabinet, Staff) получили
// согласованный результат без локальных копий.
//
// MOD: extras несут поле rate. Primary - rate не несёт: доменное
// правило игнорирует ставку у primary (эффективно = 1). EmployeeCardModal
// использует rate для бейджа «N ст.».

import { COMPANY_NAME } from "./constants";

export function getPositionInDept(employee, deptId) {
  if (!employee) return { position: "Сотрудник", isExtra: false };

  const isKbChief =
    Array.isArray(employee.roles) &&
    employee.roles.includes("kb_chief") &&
    Array.isArray(employee.kbIds) &&
    employee.kbIds.length > 0;

  if (!deptId) {
    return { position: employee.position || "Сотрудник", isExtra: false };
  }

  if (isKbChief) {
    const entry = (employee.departments || []).find((d) => d.deptId === deptId);
    const position = entry?.position || employee.position || "Сотрудник";
    return { position, isExtra: true };
  }

  const entry = (employee.departments || []).find((d) => d.deptId === deptId);

  if (!entry) {
    return { position: employee.position || "Сотрудник", isExtra: false };
  }

  if (entry.primary) {
    return {
      position: entry.position || employee.position || "Сотрудник",
      isExtra: false,
    };
  }

  return {
    position: entry.position || employee.position || "Сотрудник",
    isExtra: true,
  };
}

export function buildEmployeePositions(employee, db) {
  if (!employee || !db) return [];

  const departments = employee.departments || [];
  const result = [];

  const isKbChief =
    Array.isArray(employee.roles) &&
    employee.roles.includes("kb_chief") &&
    Array.isArray(employee.kbIds) &&
    employee.kbIds.length > 0;

  if (isKbChief) {
    const kbNames = employee.kbIds
      .map((id) => db.kbs?.find((k) => k.id === id)?.name)
      .filter(Boolean);

    result.push({
      kind: "primary",
      orgName: kbNames.join(", "),
      position: employee.position || "Главный конструктор",
    });

    for (const d of departments) {
      const dept = db.departments?.find((x) => x.id === d.deptId);
      if (!dept) continue;
      result.push({
        kind: "extra",
        orgName: dept.name,
        position: d.position || employee.position || "",
        rate: d.rate ?? 1,
      });
    }

    return result;
  }

  const primaryDept = departments.find((d) => d.primary) || departments[0];
  if (primaryDept) {
    const dept = db.departments?.find((x) => x.id === primaryDept.deptId);
    if (dept) {
      result.push({
        kind: "primary",
        orgName: dept.name,
        position: primaryDept.position || employee.position || "",
      });
    }
    for (const d of departments) {
      if (d === primaryDept) continue;
      const dd = db.departments?.find((x) => x.id === d.deptId);
      if (!dd) continue;
      result.push({
        kind: "extra",
        orgName: dd.name,
        position: d.position || employee.position || "",
        rate: d.rate ?? 1,
      });
    }
    return result;
  }

  result.push({
    kind: "primary",
    orgName: COMPANY_NAME,
    position: employee.position || "",
  });
  return result;
}

export function formatPositionLabel(pos) {
  if (!pos) return "";
  const { orgName, position } = pos;
  if (!orgName && !position) return "";
  if (!orgName) return position;
  if (!position) return orgName;
  return `${orgName} - ${position}`;
}

export function getEmployeePrimaryDepartment(employee, db) {
  if (!employee || !db) return null;

  if (
    Array.isArray(employee.roles) &&
    employee.roles.includes("kb_chief") &&
    Array.isArray(employee.kbIds) &&
    employee.kbIds.length > 0
  ) {
    const kbs = employee.kbIds
      .map((id) => db.kbs?.find((k) => k.id === id))
      .filter(Boolean);
    if (kbs.length > 0) {
      return kbs.length === 1
        ? kbs[0]
        : {
            id: kbs.map((k) => k.id).join(","),
            name: kbs.map((k) => k.name).join(", "),
            type: "kb",
          };
    }
  }

  if (employee.departments && employee.departments.length > 0) {
    const primaryDept =
      employee.departments.find((d) => d.primary) || employee.departments[0];
    if (primaryDept) {
      const dept = db.departments?.find((d) => d.id === primaryDept.deptId);
      if (dept) return dept;
    }
  }

  return { id: null, name: COMPANY_NAME, type: "company" };
}

export function getPrimaryDeptName(employee, db) {
  const dept = getEmployeePrimaryDepartment(employee, db);
  return dept ? dept.name : "-";
}
